import { useState } from "react";

const features = [
  {
    icon: "ri-shield-check-line",
    title: "End-to-End Encryption",
    desc: "All data is encrypted using AES-256 standards, ensuring your personal information and browsing activity remain completely private and inaccessible to third parties.",
  },
  {
    icon: "ri-server-line",
    title: "Zero-Log Architecture",
    desc: "Our VPN operates on a strict no-logging policy. We collect no usage data, connection logs, or timestamps — your activity stays yours alone.",
  },
  {
    icon: "ri-leaf-line",
    title: "Smart Junk Removal",
    desc: "The Cleaner app uses intelligent scanning algorithms to identify and safely remove temporary files, cache data, and app leftovers without touching important files.",
  },
  {
    icon: "ri-gallery-line",
    title: "Duplicate Photo Detection",
    desc: "AI-powered image comparison identifies duplicate and near-duplicate photos, helping you reclaim gigabytes of storage with a single tap.",
  },
  {
    icon: "ri-flashlight-line",
    title: "Real-Time Performance Boost",
    desc: "Monitor your device's performance in real time. FUSA Cleaner identifies resource-heavy processes and helps you keep your iPhone running at peak speed.",
  },
  {
    icon: "ri-wifi-off-line",
    title: "Public Wi-Fi Protection",
    desc: "Automatically activates VPN protection when connecting to unsecured public networks, keeping your sensitive data safe in cafes, airports, and hotels.",
  },
  {
    icon: "ri-user-heart-line",
    title: "Privacy Dashboard",
    desc: "A clear, intuitive privacy dashboard gives you full visibility and control over your data exposure, app permissions, and security status at a glance.",
  },
  {
    icon: "ri-settings-4-line",
    title: "Seamless iOS Integration",
    desc: "Built entirely with native iOS frameworks, our apps integrate deeply with the Apple ecosystem — Widgets, Siri Shortcuts, and iCloud sync included.",
  },
];

export default function FeaturesSection() {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  return (
    <section id="features" className="w-full bg-white py-24 px-6 md:px-16">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-xs font-semibold tracking-[0.3em] text-emerald-600 uppercase border border-emerald-200 rounded-full px-5 py-2 mb-6">
            What We Build
          </span>
          <h2 className="text-4xl md:text-6xl font-extrabold text-gray-900 leading-tight tracking-tight mb-4">
            Core Features
            <br />
            <span className="text-stone-400">of Our Apps</span>
          </h2>
          <p className="text-stone-500 text-base md:text-lg max-w-xl mx-auto leading-relaxed">
            Every feature is carefully engineered to deliver a seamless, safe, and high-performance experience on iOS devices.
          </p>
        </div>

        {/* Feature Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {features.map((feat, idx) => (
            <div
              key={feat.title}
              onMouseEnter={() => setHoveredIdx(idx)}
              onMouseLeave={() => setHoveredIdx(null)}
              className={`rounded-2xl p-7 cursor-default transition-all duration-300 ${
                hoveredIdx === idx
                  ? "bg-gray-950 text-white"
                  : "bg-stone-50 text-gray-900"
              }`}
            >
              <div
                className={`w-11 h-11 flex items-center justify-center rounded-xl mb-5 transition-colors duration-300 ${
                  hoveredIdx === idx ? "bg-emerald-400/20" : "bg-stone-200"
                }`}
              >
                <i
                  className={`${feat.icon} text-xl transition-colors duration-300 ${
                    hoveredIdx === idx ? "text-emerald-400" : "text-stone-600"
                  }`}
                />
              </div>
              <h3
                className={`font-bold text-sm mb-2 leading-snug transition-colors duration-300 ${
                  hoveredIdx === idx ? "text-white" : "text-gray-900"
                }`}
              >
                {feat.title}
              </h3>
              <p
                className={`text-xs leading-relaxed transition-colors duration-300 ${
                  hoveredIdx === idx ? "text-white/60" : "text-stone-500"
                }`}
              >
                {feat.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
